import {
  Button,
  Empty,
  Flex,
  Popover,
  Select,
  Space,
  Tag,
  Typography
} from 'antd'
import type { TableColumnsType } from 'antd'
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import {
  organizationsAccessListInfiniteOptions,
  type OrganizationsAccessListData,
  type PaginatedOrganizationUsersList
} from 'src/api/main'
import {
  ContentSpinner,
  DateTimeCell,
  InfiniteSelect,
  ProTableContainer,
  QueryErrorAlert,
  RevokeCredentialModal,
  TableActionColumnTitle
} from 'src/components'
import { FILTER_CONTROL_WIDTH } from 'src/constants'
import type { WorkspaceUser } from 'src/features/users/types'
import { parseAllowedScopes } from 'src/features/workspaces/components/agents/scopeModel'
import {
  DEFAULT_PER_PAGE,
  DEFAULT_PER_PAGE_OPTIONS,
  useQueryUpdate
} from 'src/hooks'
import type { OptionTransformType } from 'src/utils'
import { composeDisplayName } from 'src/utils/string'

import {
  StatusPill,
  useCredentialsListStyles
} from '../../components/credentials'

import { ConnectionScopesModal } from './ConnectionScopesModal'
import { useMcpConnections, useRevokeMcpConnection } from './hooks'
import { MCP_SERVICES, parseConnectionFilters, revokeReason } from './model'
import type { ConnectionsScope, McpConnection } from './types'

const { Text } = Typography

type Props = {
  workspaceId: number
  scope: ConnectionsScope
  /** The toolbar's primary action (Connect). */
  action?: ReactNode
}

export const ConnectionsTable = ({ workspaceId, scope, action }: Props) => {
  const { t } = useTranslation()
  const { styles } = useCredentialsListStyles()
  const { params, updateQuery } = useQueryUpdate()
  const filters = scope === 'org' ? parseConnectionFilters(params) : {}
  const {
    data: connections = [],
    isLoading,
    isFetching,
    isError,
    refetch
  } = useMcpConnections(workspaceId, scope, filters)
  const revoke = useRevokeMcpConnection(workspaceId, scope)
  const [target, setTarget] = useState<McpConnection | null>(null)
  const [editing, setEditing] = useState<McpConnection | null>(null)

  const handleRevoke = async (reason: string | null) => {
    if (!target) return
    const result = await revoke
      .mutateAsync({ id: target.id, reason })
      .catch(() => null)
    if (result) setTarget(null)
  }

  const columns: TableColumnsType<McpConnection> = [
    {
      title: t('workspace.aiConnectors.connections.columns.client'),
      key: 'client',
      fixed: 'left',
      width: 240,
      render: (_, item) => (
        <Flex vertical>
          <Text strong ellipsis={{ tooltip: item.name }}>
            {item.name}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t(`workspace.aiConnectors.connections.kind.${item.kind}`)}
          </Text>
        </Flex>
      )
    },
    ...(scope === 'org'
      ? [
          {
            title: t('workspace.aiConnectors.connections.columns.user'),
            key: 'user',
            width: 220,
            render: (_: unknown, item: McpConnection) => (
              <Text ellipsis={{ tooltip: item.user.email }}>
                {item.user.full_name || item.user.email}
              </Text>
            )
          }
        ]
      : []),
    {
      title: t('workspace.aiConnectors.connections.columns.service'),
      key: 'service',
      width: 140,
      render: (_, item) =>
        t(`workspace.aiConnectors.services.${item.service}.name`)
    },
    {
      title: t('workspace.aiConnectors.connections.columns.scopes'),
      key: 'scopes',
      width: 140,
      render: (_, item) => {
        const codes = [...parseAllowedScopes(item.scopes)]
        if (!codes.length) return <Text type="secondary">—</Text>

        return (
          <Popover
            trigger={['hover', 'focus']}
            content={
              <Space vertical size={4} style={{ maxWidth: 320 }}>
                {codes.map((code) => (
                  <Text key={code} code style={{ fontSize: 12 }}>
                    {code}
                  </Text>
                ))}
              </Space>
            }
          >
            <Tag tabIndex={0} style={{ cursor: 'default' }}>
              {t('workspace.aiConnectors.connections.scopesCount', {
                count: codes.length
              })}
            </Tag>
          </Popover>
        )
      }
    },
    {
      title: t('workspace.aiConnectors.connections.columns.status'),
      key: 'status',
      width: 200,
      render: (_, item) => {
        const reason =
          item.status === 'revoked' ? revokeReason(item.revoke_reason) : null
        if (!reason) return <StatusPill status={item.status} />
        const label = t(
          `workspace.aiConnectors.connections.revokeReasons.${reason}`
        )

        return (
          <Flex vertical gap={2} align="flex-start">
            <StatusPill status={item.status} />
            <Text
              type="secondary"
              style={{ fontSize: 12, maxWidth: '100%' }}
              ellipsis={{ tooltip: item.revoke_note || label }}
            >
              {label}
            </Text>
          </Flex>
        )
      }
    },
    {
      title: t('workspace.aiConnectors.connections.columns.created'),
      key: 'created',
      width: 160,
      render: (_, item) => <DateTimeCell value={item.created_at} />
    },
    {
      title: t('workspace.aiConnectors.connections.columns.lastUsed'),
      key: 'lastUsed',
      width: 160,
      render: (_, item) => (
        <DateTimeCell
          value={item.last_used_at}
          variant="relative"
          empty={t('workspace.credentials.neverUsed')}
        />
      )
    },
    {
      title: <TableActionColumnTitle />,
      key: 'actions',
      width: 180,
      align: 'center',
      fixed: 'right',
      render: (_, item) => (
        <Flex justify="center" gap={4}>
          <Button
            size="small"
            type="text"
            disabled={item.status !== 'active'}
            onClick={() => setEditing(item)}
          >
            {t('workspace.aiConnectors.connections.editScopes')}
          </Button>
          <Button
            size="small"
            type="text"
            danger
            disabled={item.status !== 'active'}
            onClick={() => setTarget(item)}
          >
            {t('workspace.aiConnectors.connections.revoke')}
          </Button>
        </Flex>
      )
    }
  ]

  const count = (
    <span className={styles.count}>
      <strong>{connections.length}</strong>{' '}
      <Text type="secondary">
        {t('workspace.aiConnectors.connections.countLabel')}
      </Text>
    </span>
  )

  const orgFilters = (
    <Flex align="center" gap={8} wrap="wrap">
      <Select
        size="small"
        allowClear
        placeholder={t('workspace.aiConnectors.connections.filters.service')}
        value={filters.service}
        onChange={(value) => updateQuery({ service: value ?? null })}
        style={{ width: FILTER_CONTROL_WIDTH }}
        options={MCP_SERVICES.map((service) => ({
          value: service,
          label: t(`workspace.aiConnectors.services.${service}.name`)
        }))}
      />
      <InfiniteSelect<
        OrganizationsAccessListData,
        PaginatedOrganizationUsersList,
        WorkspaceUser
      >
        size="small"
        allowClear
        style={{ width: 220 }}
        value={filters.user}
        onChange={(value) =>
          updateQuery({ user: typeof value === 'number' ? value : null })
        }
        getQueryFunction={organizationsAccessListInfiniteOptions}
        baseOptions={{ query: { organization: workspaceId } }}
        getOptions={({ options }: OptionTransformType<WorkspaceUser>) =>
          options.map((access) => ({
            value: access.user.id,
            label: composeDisplayName(
              access.user.first_name,
              access.user.last_name,
              access.user.email
            )
          }))
        }
        placeholder={t('workspace.aiConnectors.connections.filters.user')}
      />
    </Flex>
  )

  if (isLoading) return <ContentSpinner minHeight={240} />

  return (
    <Flex vertical gap={14}>
      {isError ? <QueryErrorAlert onRetry={() => void refetch()} /> : null}
      <ProTableContainer<McpConnection>
        showSearch={false}
        tableIdentifier={`workspace-ai-connections-${scope}`}
        className={styles.table}
        tableLayout="fixed"
        loading={isFetching}
        rowKey="id"
        columns={columns}
        dataSource={connections}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t(
                    scope === 'mine'
                      ? 'workspace.aiConnectors.connections.emptyMine'
                      : 'workspace.aiConnectors.connections.empty'
                  )}
                </Text>
              }
            />
          )
        }}
        pagination={{
          defaultPageSize: DEFAULT_PER_PAGE,
          pageSizeOptions: DEFAULT_PER_PAGE_OPTIONS.map(String),
          size: 'small',
          showSizeChanger: true,
          hideOnSinglePage: true
        }}
        scroll={{ x: 1100 }}
        leftChildren={scope === 'org' ? orgFilters : count}
        rightChildren={
          scope === 'org' ? (
            <Flex align="center" gap={12}>
              {count}
              {action}
            </Flex>
          ) : (
            action
          )
        }
      />

      {editing ? (
        <ConnectionScopesModal
          key={editing.id}
          workspaceId={workspaceId}
          scope={scope}
          connection={editing}
          onClose={() => setEditing(null)}
        />
      ) : null}

      <RevokeCredentialModal
        open={target !== null}
        title={t('workspace.aiConnectors.connections.revokeTitle')}
        description={t('workspace.aiConnectors.connections.revokeBody')}
        okText={t('workspace.aiConnectors.connections.revoke')}
        loading={revoke.isPending}
        onConfirm={handleRevoke}
        onCancel={() => setTarget(null)}
      />
    </Flex>
  )
}
